"use client";

import { useState } from "react";
import { generateScript, CreatorVoiceError, type ScriptResult } from "@/lib/creatorVoice/api";
import { inputClass, ErrorBox } from "@/components/creatorVoice/CreatorVoiceUI";

const TEMPLATES = [
  { id: "listicle", label: "Listicle" },
  { id: "story", label: "Story / Narrative" },
  { id: "howto", label: "How-To" },
  { id: "hook_first", label: "Hook-First" },
];
const TONES = ["energetic", "calm", "funny", "serious", "inspiring"];
const LENGTHS = [
  { id: "short", label: "Short (~30s)" },
  { id: "medium", label: "Medium (~60s)" },
  { id: "long", label: "Long (~90s+)" },
];

export default function ScriptWriterTool() {
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("energetic");
  const [length, setLength] = useState("short");
  const [template, setTemplate] = useState("listicle");
  const [result, setResult] = useState<ScriptResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    if (!topic.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await generateScript(topic.trim(), tone, length, template));
    } catch (e) {
      setError(e instanceof CreatorVoiceError ? e.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">What's your video about?</label>
      <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={3} placeholder="e.g. 5 productivity tips for remote workers" className={inputClass} />

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Template</label>
          <select value={template} onChange={(e) => setTemplate(e.target.value)} className={inputClass}>
            {TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Tone</label>
          <select value={tone} onChange={(e) => setTone(e.target.value)} className={inputClass}>
            {TONES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Length</label>
          <select value={length} onChange={(e) => setLength(e.target.value)} className={inputClass}>
            {LENGTHS.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={generate}
          disabled={loading || !topic.trim()}
          className="rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
        >
          {loading ? "Writing…" : "Generate Script"}
        </button>
      </div>

      {error ? <ErrorBox message={error} /> : null}

      {result ? (
        <div className="mt-4 space-y-3">
          {(["hook", "body", "ending", "cta"] as const).map((key) => (
            <div key={key} className="rounded-xl border border-rose-200/80 bg-rose-50/60 p-4 dark:border-rose-800/60 dark:bg-rose-500/5">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">{key}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{result[key]}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
